import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = ["list", "status"]
  static values = { url: String, eventName: String, refresh: Boolean }

  connect() {
    this.persistedIds = this.ids()
    this.sortable = Sortable.create(this.listTarget, {
      handle: ".handle", // optional, if you want drag handles
      animation: 150,
      onEnd: () => this.persistOrder(),
    })
  }

  disconnect() {
    this.sortable?.destroy()
    this.sortable = null
  }

  move(event) {
    if (this.saving) return

    const item = event.currentTarget.closest("[data-id]")
    const direction = event.params.direction
    const sibling = direction === "earlier" ? item.previousElementSibling : item.nextElementSibling

    if (!sibling) return

    if (direction === "earlier") {
      this.listTarget.insertBefore(item, sibling)
    } else {
      this.listTarget.insertBefore(sibling, item)
    }

    this.persistOrder()
  }

  async persistOrder() {
    if (this.saving) return

    const ids = this.ids()
    this.saving = true
    this.sortable.option("disabled", true)
    this.setStatus("Saving order…")

    try {
      const headers = { "Content-Type": "application/json" }
      if (this.refreshValue) headers.Accept = "text/vnd.turbo-stream.html"
      const csrfToken = document.querySelector("[name=csrf-token]")?.content
      if (csrfToken) headers["X-CSRF-Token"] = csrfToken

      const response = await fetch(this.urlValue, {
        method: "POST",
        headers,
        body: JSON.stringify({ ids })
      })

      if (!response.ok) throw new Error(`Reorder failed with status ${response.status}`)

      const streamMessage = this.refreshValue ? await response.text() : null
      this.persistedIds = ids
      this.setStatus("Order saved")

      if (this.hasEventNameValue) {
        document.dispatchEvent(new CustomEvent(this.eventNameValue, { detail: { ids } }))
      }

      if (streamMessage) Turbo.renderStreamMessage(streamMessage)
    } catch (error) {
      this.restorePersistedOrder()
      this.setStatus("Could not save the new order. Please try again.", true)
      console.error(error)
    } finally {
      this.saving = false
      this.sortable?.option("disabled", false)
    }
  }

  ids() {
    return Array.from(this.listTarget.children).map(element => element.dataset.id)
  }

  restorePersistedOrder() {
    const elements = new Map(
      Array.from(this.listTarget.children).map(element => [element.dataset.id, element])
    )

    this.persistedIds.forEach(id => this.listTarget.append(elements.get(id)))
  }

  setStatus(message, error = false) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle("text-danger", error)
    this.statusTarget.classList.toggle("text-muted", !error)
  }
}
